// src/lib/activeRound.js
// Pure, framework-free helpers for the Home active-round resume tile and the
// scorecard resurrection gate. Extracted here (out of the ~600-line HomePage /
// ScorecardPage components) so the discriminated-clear decision, the payload
// parse, and the poll scheduler can all be unit-tested without a React or DOM
// harness. See ops #42 (staleness/phantom) + #43 (auto-refresh).

// ---------------------------------------------------------------------------
// Discriminated active-round outcome.
//
// The whole point of ops #42's goal (b): the client MUST be able to tell
// "the server POSITIVELY reports no active round" apart from "the active-round
// query ERRORED / we couldn't reach the server". Only the FORMER may
// authoritatively clear the resume tile + localStorage; the latter must leave
// whatever is on screen untouched (a transient blip must not wipe a real
// resume card, and must not leave a dead tile showing either).
//
// home-bootstrap.js encodes this in the shape of its `active_round` field:
//   • object  → a live round exists            → 'found'
//   • null    → server positively reports none → 'empty'  (authoritative clear)
//   • undefined / key absent → the query errored (sentinel omitted by
//                              JSON.stringify) → 'unknown' (keep on screen)
//
// The /db fallback path funnels through the same classifier: a settled-and-
// fulfilled get_active_round gives object|null (found|empty); a rejected one is
// mapped to undefined by the caller so it classifies as 'unknown'.
export function classifyActiveRound(raw) {
  if (raw === null) return 'empty'
  if (raw === undefined) return 'unknown'
  if (typeof raw === 'object') return 'found'
  // Any other primitive is a malformed response we can't trust → keep state.
  return 'unknown'
}

// Parse an active_rounds row ({ standalone_round_id, payload, updated_at })
// into the standaloneState object the resume tile / scorecard render from.
// Stamps the server's updated_at (ms) onto the payload so a same-device
// localStorage read can compare against a cross-device write and pick the
// fresher of the two (matches the HomePage initializer's compare). Returns
// null when there's no usable payload.
export function parseActiveRoundRow(row) {
  if (!row?.payload) return null
  const payload = typeof row.payload === 'string' ? JSON.parse(row.payload) : row.payload
  const serverAt = row.updated_at
  if (serverAt) {
    const ms = Date.parse(serverAt)
    if (Number.isFinite(ms)) payload.updated_at = ms
  }
  return payload
}

// ---------------------------------------------------------------------------
// Reload-resume for the standalone scorecard (D-300).
//
// A mid-round Android Wi-Fi↔cellular switch triggers a FULL PWA reload. That
// reload re-mounts ScorecardPage on /scorecard but DROPS React Router's
// location.state — where a standalone round's identity lives (standaloneState =
// location.state?.standalone ? location.state : null). With state gone the page
// falls through to its "No active round." screen and offers only "Start a
// Round", even though the round is perfectly alive: its identity is still parked
// in localStorage['active_round'] (written by PlayPage on round START and on an
// in-round holes/tee edit — NOT re-written by saveProgress, which mirrors scores
// to standalone_round_<player.id> and to the DB active_rounds table) and its
// scores in standalone_round_<player.id>. Home recovered it precisely because
// tapping Home re-read active_round and navigated back in — this helper lets the
// scorecard do the same thing itself, in place.
//
// Given the RAW localStorage['active_round'] string, whether router state
// survived the reload, and the CURRENTLY signed-in player id, return the parsed
// round payload to re-enter — the SAME object Home / PlayPage / App's
// ResumeToActiveRound navigate with as { ...payload, standalone: true } — or null
// when there's nothing safe to resume. Rules:
//   • hasRouterState truthy → null. Live router state always wins; never
//     override a real in-progress standaloneState with a stale localStorage blob.
//   • no/blank/garbage string → null (JSON.parse guarded).
//   • a payload without a usable id + course → null. We need a renderable round
//     identity; a truncated blob must not drive a resume into an empty scorecard.
//   • OWNER CHECK: the payload's player_id must equal the signed-in player id.
//     active_round is a single GLOBAL localStorage key (not player-scoped) and is
//     NOT cleared on sign-out, so without this a parked round from user A could be
//     resumed by user B after an account switch on the same device — and offline
//     the server liveness gate can't bounce it. Return null when the player isn't
//     resolved yet (currentPlayerId falsy) so we never resume into a half-booted
//     auth state. (A co-player scoring a friend's round carries the CREATOR's
//     player_id in this payload, so they fall back to Home's resume tile rather
//     than in-place scorecard resume — an intentionally conservative scope.)
// On WHY the presence of active_round is a sound "still resumable" signal: the
// SOLO finish/exit paths (saveAndExit, deleteAllAndExit, the delete flow, the
// dead-round liveness branch) all remove the key, and HomePage's reconcile clears
// it when the server POSITIVELY reports the round gone — so a completed solo round
// is already absent here. The MULTIPLAYER walk-off paths (saveMyScoreAndExit /
// deleteMyScoreAndExit) intentionally LEAVE the key because the group's round
// stays live for everyone else; the server liveness gate in ScorecardPage
// (get_active_round_by_id → bounce) is the backstop that retires a round the local
// key outlived. This helper never auto-starts or mutates anything.
export function readResumableStandaloneRound(rawActiveRound, hasRouterState, currentPlayerId) {
  if (hasRouterState) return null
  if (!currentPlayerId) return null
  if (!rawActiveRound) return null
  let ar = null
  try { ar = JSON.parse(rawActiveRound) } catch { return null }
  if (!ar || typeof ar !== 'object') return null
  if (!ar.id || !ar.course) return null
  if (ar.player_id !== currentPlayerId) return null
  return ar
}

// ---------------------------------------------------------------------------
// Resurrection gate (ops #42 goal c, client-side no-DDL mitigation).
//
// When the scorecard is opened from a CACHED/resumed state (Home tile,
// cold-open auto-nav, another device's stale localStorage) we confirm the
// round is still live server-side before letting it write active_rounds. This
// returns true ONLY when the server has POSITIVELY told us the resumed round is
// no longer live — a transient error never trips it (that would bounce a
// legitimate offline resume).
//
// The caller queries db('get_active_round_by_id', { standalone_round_id }),
// which is scoped to the EXACT round the scorecard was opened with — NOT the
// caller's single most-recent active round. That scoping is what fixes the
// false-positive kill for a player who legitimately holds two concurrent
// active_rounds (a solo round AND a co-player seat in a friend's round): asking
// for the most-recent one would return the wrong round and evict the other.
//
//   result: the value returned by db('get_active_round_by_id') — the row for
//           THIS round when it's still live, or null when THIS specific round
//           is gone/ended for the caller.
//   error:  truthy when the call threw (network/etc).
//   roundId: the standalone_round_id the scorecard was opened with.
export function isResumedRoundDead({ result, error, roundId }) {
  if (error) return false          // couldn't reach the server → never bounce
  if (result === undefined) return false // no positive answer → keep the round
  if (result === null) return true // server says THIS round is no longer live
  // A row came back. Since the query is scoped to standalone_round_id, any live
  // row means this round is still active → not dead. (A mismatched id would be
  // an unexpected response; we still keep the round rather than risk bouncing a
  // live one — safer than the old most-recent check, which treated any id
  // mismatch as dead and could evict a legitimate concurrent round.)
  return false
}

// ---------------------------------------------------------------------------
// Creator-delete handoff (D-279, ops #50 — full #42 closure).
//
// When a round CREATOR deletes a multiplayer round that still has OTHER active
// AUTHENTICATED players scoring, the round is SOFT-deleted (deleted_at set)
// instead of hard-deleted, so those players can consent to a hand-off and one
// of them can grab ownership. These three pure helpers hold the decision logic
// so it's unit-testable without a DB or React.

// The hand-off eligibility window, in milliseconds. A soft-deleted round is
// grabbable — and its "recent activity" for the qualifying-co-player check is
// measured — inside this window. Single source of truth so the server delete
// branch, the liveness classifier, and the grab gate all agree.
export const HANDOFF_WINDOW_MS = 15 * 60 * 1000 // 15 minutes

// Given the co-players enumerated for a round, return the subset that QUALIFIES
// for a hand-off: OTHER players (not the creator/deleter), each with a linked
// account (auth_user_id present — a stub has no grab path), and only when the
// round itself had recent activity (roundUpdatedAt within the window). We use
// the round-level active_rounds.updated_at as the activity signal because
// round_scores carries no per-player save timestamp (see the blocked-on-owner
// flag in the task for the true-fidelity column); this is a round-level
// approximation, not per-player save recency.
//
//   coPlayers:      [{ player_id, auth_user_id, name? }, ...] (round_scores join players)
//   excludeIds:     player ids to drop (the creator/owner + the verified deleter)
//   roundUpdatedAt: active_rounds.updated_at (ISO string, Date, or ms)
//   now / windowMs: injectable for tests
// Returns the (de-duplicated) qualifying co-players — the recipients of the
// "host ended the round" push. Empty array => hard-delete (no hand-off).
export function qualifyingAuthCoPlayers({
  coPlayers,
  excludeIds = [],
  roundUpdatedAt,
  now = Date.now(),
  windowMs = HANDOFF_WINDOW_MS,
}) {
  if (!roundUpdatedAt) return [] // no active_rounds row -> nothing live to hand off
  const t = typeof roundUpdatedAt === 'number' ? roundUpdatedAt : Date.parse(roundUpdatedAt)
  if (!Number.isFinite(t) || now - t > windowMs) return [] // stale / unparsable -> no hand-off
  const exclude = new Set(excludeIds.filter(Boolean).map(String))
  const seen = new Set()
  const out = []
  for (const cp of coPlayers || []) {
    if (!cp || !cp.player_id) continue
    const pid = String(cp.player_id)
    if (exclude.has(pid) || seen.has(pid)) continue
    if (!cp.auth_user_id) continue // stub co-player: no account, no grab path
    seen.add(pid)
    out.push(cp)
  }
  return out
}

// Classify the liveness of ONE specific round for the caller, given the
// active_rounds row (already access-scoped to the caller by the query) joined
// with standalone_rounds.deleted_at. Three states the client distinguishes:
//   • LIVE               — row exists and not soft-deleted -> keep scoring.
//   • DELETED_GRABBABLE  — soft-deleted within the window -> offer the hand-off.
//   • DEAD               — no row (hard-deleted / not the caller's round), or a
//                          soft-delete older than the window -> bounce to Home.
// `row` null/absent is DEAD. An unparsable deleted_at is treated as LIVE (never
// bounce a live round over a bad timestamp — the safe direction, matching
// isResumedRoundDead's error posture).
export function classifyRoundLiveness({ row, now = Date.now(), windowMs = HANDOFF_WINDOW_MS }) {
  if (!row) return 'DEAD'
  if (row.deleted_at == null) return 'LIVE'
  const t = typeof row.deleted_at === 'number' ? row.deleted_at : Date.parse(row.deleted_at)
  if (!Number.isFinite(t)) return 'LIVE'
  return now - t <= windowMs ? 'DELETED_GRABBABLE' : 'DEAD'
}

// Grab-eligibility gate (server-authoritative; also drives the client's decision
// to even attempt a grab). A caller may grab a soft-deleted round ONLY when:
//   • they are AUTHENTICATED (a stub has no grab path — auth-only end to end),
//   • they were IN the round (owner OR a linked player OR held a round_scores
//     row — resolved by the caller), and
//   • the soft-delete is still inside the window.
// First-write-wins on a concurrent grab is enforced separately by the guarded
// UPDATE in the server; this predicate is the pre-check both sides share.
export function canGrabRound({
  callerAuthed,
  wasInRound,
  deletedAt,
  now = Date.now(),
  windowMs = HANDOFF_WINDOW_MS,
}) {
  if (!callerAuthed) return false
  if (!wasInRound) return false
  if (deletedAt == null) return false // not soft-deleted -> nothing to grab
  const t = typeof deletedAt === 'number' ? deletedAt : Date.parse(deletedAt)
  if (!Number.isFinite(t)) return false
  return now - t <= windowMs
}

// ---------------------------------------------------------------------------
// Active-round poll scheduler (ops #43).
//
// A framework-free interval scheduler that the HomePage effect drives. Kept out
// of the component (and away from DOM listeners) so it's testable with fake
// timers: the caller attaches the real `visibilitychange` listener and calls
// handleVisibilityChange(); this object owns only the interval + the pause/
// resume/foreground-fetch policy.
//
//   intervalMs: poll period (~30s per #43).
//   isHidden():  returns true when the tab is backgrounded.
//   onTick(reason): the authoritative active-round reconcile; reason is
//                   'interval' or 'foreground' for logging/telemetry.
//
// Policy: while visible, fire onTick every intervalMs. While hidden, PAUSE
// (stop the interval; also guard the interval callback so a tick that fires on
// the same turn as a background transition is a no-op). On hidden→visible,
// fire onTick immediately ('foreground') and resume the interval.
export function createActiveRoundPoll({ intervalMs = 30000, isHidden, onTick }) {
  let timer = null
  const hidden = () => (typeof isHidden === 'function' ? isHidden() : false)
  const startInterval = () => {
    if (timer != null) return
    timer = setInterval(() => { if (!hidden()) onTick('interval') }, intervalMs)
  }
  const stopInterval = () => {
    if (timer == null) return
    clearInterval(timer)
    timer = null
  }
  return {
    // Begin polling if the tab is currently visible; stay idle if hidden
    // (the next foreground transition starts it).
    start() {
      if (!hidden()) startInterval()
    },
    stop() { stopInterval() },
    // Wire this to a `visibilitychange` listener.
    handleVisibilityChange() {
      if (hidden()) {
        stopInterval()
      } else {
        onTick('foreground')
        startInterval()
      }
    },
    // Test/introspection only.
    _isRunning() { return timer != null },
  }
}
